import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, User, Target, Sparkles,
  Cpu, Wrench, X, Plus, ChevronRight
} from "lucide-react";
import { workforceApi } from "../../lib/workforce";
import BrainSelector from "../../components/workforce/BrainSelector";

const STEPS = [
  { id: "identity",    label: "Identity",     icon: User,     hint: "Who is this worker?" },
  { id: "mission",     label: "Mission",      icon: Target,   hint: "What do they do?" },
  { id: "persona",     label: "Persona",      icon: Sparkles, hint: "How do they think?" },
  { id: "brain",       label: "Brain",        icon: Cpu,      hint: "Which model powers them?" },
  { id: "tools",       label: "Tools",        icon: Wrench,   hint: "What can they use?" },
  { id: "review",      label: "Review",       icon: Check,    hint: "Ship it 🚀" },
];

const ROLE_TYPES = [
  "Researcher", "Analyst", "Manager", "Writer", "Developer",
  "Sales", "Marketing", "Operations", "Finance", "Customer Support", "Custom",
];

const TEMPLATES = [
  { name: "Startup CFO",           avatar: "💼", title: "Chief Financial Officer",  dept: "Finance",      role: "Finance",
    mission: "Advise on financial strategy, forecasting, and unit economics.",
    persona: "Sharp, direct, allergic to fluff. Explains numbers so anyone can understand.", brain: "claude-sonnet-4-6" },
  { name: "Research Analyst",      avatar: "🔬", title: "Senior Research Analyst",  dept: "Strategy",     role: "Researcher",
    mission: "Investigate topics thoroughly and produce evidence-backed briefings.",
    persona: "Curious, structured, always cites sources.", brain: "gpt-5-1" },
  { name: "Copywriter",            avatar: "✍️", title: "Senior Copywriter",         dept: "Marketing",    role: "Writer",
    mission: "Turn ideas into punchy, on-brand copy across channels.",
    persona: "Playful, human, allergic to corporate speak.", brain: "kimi-k2" },
  { name: "SDR",                   avatar: "🎯", title: "Sales Development Rep",     dept: "Sales",        role: "Sales",
    mission: "Qualify leads and craft outbound messages that get replies.",
    persona: "Warm, persistent, laser focused on the prospect's problem.", brain: "qwen-72b" },
  { name: "Code Reviewer",         avatar: "🧑‍💻", title: "Staff Software Engineer",  dept: "Engineering",  role: "Developer",
    mission: "Review code for bugs, security, style, and clarity.",
    persona: "Rigorous, kind, always suggests a fix — not just a critique.", brain: "deepseek-chat" },
  { name: "Ops Analyst",           avatar: "📊", title: "Operations Analyst",        dept: "Operations",   role: "Analyst",
    mission: "Analyze process data and identify bottlenecks and cost savings.",
    persona: "Analytical, evidence-based, flags uncertainty explicitly.", brain: "gpt-4o-mini" },
];

export default function CreateWorker() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    avatar: "🧑‍💼",
    title: "",
    department: "General",
    role_type: "Custom",
    description: "",
    mission: "",
    responsibilities: [],
    success_criteria: [],
    persona: {
      background: "",
      expertise: [],
      freeform: "",
      sliders: { analytical: 50, detail: 50, formality: 50, speed: 50, risk: 50 },
    },
    instructions: { system: "", operating_rules: "", quality_standards: "", output_requirements: "", do_not_do: "" },
    brain: { brain_id: "gpt-4o-mini", temperature: 0.7, max_tokens: 1500, streaming: true },
    auto_brain: false,
    auto_brain_priority: "balanced",
    skills: [],
    tools: ["web_search"],
  });
  const [respInput, setRespInput] = useState("");
  const [succInput, setSuccInput] = useState("");
  const [expertiseInput, setExpertiseInput] = useState("");

  // Hydrate from template if one was queued via sessionStorage
  React.useEffect(() => {
    try {
      const raw = sessionStorage.getItem("wf_prefill");
      if (raw) {
        const prefill = JSON.parse(raw);
        sessionStorage.removeItem("wf_prefill");
        setForm((f) => ({ ...f, ...prefill,
          persona: { ...f.persona, ...(prefill.persona || {}) },
          instructions: { ...f.instructions, ...(prefill.instructions || {}) },
          brain: { ...f.brain, ...(prefill.brain || {}) },
        }));
      }
    } catch (err) {
      console.warn("Ignored malformed AI prefill in URL:", err);
    }
  }, []);

  const patch = (obj) => setForm((f) => ({ ...f, ...obj }));
  const patchP = (obj) => setForm((f) => ({ ...f, persona: { ...f.persona, ...obj } }));
  const patchSlider = (k, v) => setForm((f) => ({ ...f, persona: { ...f.persona, sliders: { ...f.persona.sliders, [k]: v } } }));
  const patchI = (obj) => setForm((f) => ({ ...f, instructions: { ...f.instructions, ...obj } }));
  const patchB = (obj) => setForm((f) => ({ ...f, brain: { ...f.brain, ...obj } }));

  const stepValid = useMemo(() => {
    if (step === 0) return form.name.trim() && form.title.trim();
    if (step === 1) return form.mission.trim();
    if (step === 3) return !!form.brain.brain_id;
    return true;
  }, [step, form]);

  const applyTemplate = (t) => {
    patch({
      name: t.name, avatar: t.avatar, title: t.title,
      department: t.dept, role_type: t.role,
      description: t.persona,
      mission: t.mission,
      persona: { ...form.persona, freeform: t.persona },
      brain: { ...form.brain, brain_id: t.brain },
    });
  };

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      const created = await workforceApi.createWorker(form);
      nav(`/workforce/worker/${created.worker_id}`, { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to create worker");
    } finally {
      setBusy(false);
    }
  };

  const currentStep = STEPS[step];

  return (
    <div className="min-h-screen bg-[#FDF8F5]" data-testid="create-worker-page">
      {/* Header */}
      <div className="border-b-2 border-slate-900 bg-white px-6 py-4 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <Link to="/workforce" className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-slate-900" data-testid="back-to-workforce">
            <ArrowLeft size={16} strokeWidth={2.5} /> Back
          </Link>
          <div className="text-sm font-heading font-bold">Hire a new worker</div>
          <div className="text-xs text-slate-500 font-bold uppercase tracking-widest">
            Step {step + 1} / {STEPS.length}
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="max-w-4xl mx-auto px-6 pt-6" data-testid="wizard-stepper">
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < step;
            const current = i === step;
            return (
              <React.Fragment key={s.id}>
                <button
                  type="button"
                  disabled={i > step && !done}
                  onClick={() => (done || i <= step) && setStep(i)}
                  data-testid={`step-${s.id}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 font-heading font-bold text-sm whitespace-nowrap transition-all ${
                    current ? "bg-slate-900 text-white border-slate-900 shadow-brut-sm" :
                    done    ? "bg-emerald-100 text-emerald-800 border-emerald-700" :
                              "bg-white text-slate-500 border-slate-300"
                  }`}
                >
                  <Icon size={14} strokeWidth={2.5} />
                  {s.label}
                </button>
                {i < STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300 shrink-0" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Main */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="brut-card p-6 md:p-8">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">{currentStep.hint}</div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-6">{currentStep.label}</h2>

          {/* STEP 0 — Identity */}
          {step === 0 && (
            <div className="space-y-4" data-testid="step-identity-body">
              {/* Templates */}
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Quick start · pick a template</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {TEMPLATES.map((t, i) => (
                    <button
                      key={t.name}
                      type="button"
                      data-testid={`template-${i}`}
                      onClick={() => applyTemplate(t)}
                      className="flex items-center gap-2 p-2.5 rounded-xl border-2 border-slate-300 hover:border-slate-900 hover:bg-amber-50 text-left transition-colors"
                    >
                      <span className="text-2xl">{t.avatar}</span>
                      <div>
                        <div className="font-heading font-bold text-sm">{t.name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{t.title}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mt-6">…or build from scratch</div>
              <div className="flex gap-3">
                <input
                  data-testid="worker-avatar"
                  value={form.avatar}
                  onChange={(e) => patch({ avatar: e.target.value })}
                  className="brut-input w-20 text-3xl text-center py-3"
                  maxLength={4}
                />
                <input
                  data-testid="worker-name"
                  value={form.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder="Worker name (e.g., Sarah Mitchell)"
                  className="brut-input flex-1"
                />
              </div>
              <input
                data-testid="worker-title"
                value={form.title}
                onChange={(e) => patch({ title: e.target.value })}
                placeholder="Job title (e.g., Senior Supply Chain Analyst)"
                className="brut-input"
              />
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  data-testid="worker-department"
                  value={form.department}
                  onChange={(e) => patch({ department: e.target.value })}
                  placeholder="Department"
                  className="brut-input"
                />
                <select
                  data-testid="worker-role"
                  value={form.role_type}
                  onChange={(e) => patch({ role_type: e.target.value })}
                  className="brut-input"
                >
                  {ROLE_TYPES.map((r) => <option key={r}>{r}</option>)}
                </select>
              </div>
              <textarea
                data-testid="worker-description"
                value={form.description}
                onChange={(e) => patch({ description: e.target.value })}
                placeholder="Short description of this worker (a sentence or two)"
                rows={3}
                className="brut-input resize-none"
              />
            </div>
          )}

          {/* STEP 1 — Mission */}
          {step === 1 && (
            <div className="space-y-4" data-testid="step-mission-body">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1 block">Primary Mission</label>
                <textarea
                  data-testid="worker-mission"
                  value={form.mission}
                  onChange={(e) => patch({ mission: e.target.value })}
                  placeholder="What is this worker's main purpose?"
                  rows={3}
                  className="brut-input resize-none"
                />
              </div>
              <ChipList
                label="Responsibilities"
                items={form.responsibilities}
                onAdd={(v) => patch({ responsibilities: [...form.responsibilities, v] })}
                onRemove={(i) => patch({ responsibilities: form.responsibilities.filter((_, ix) => ix !== i) })}
                inputValue={respInput}
                setInputValue={setRespInput}
                placeholder="Add a responsibility and press Enter"
                testid="responsibility"
              />
              <ChipList
                label="Success Criteria"
                items={form.success_criteria}
                onAdd={(v) => patch({ success_criteria: [...form.success_criteria, v] })}
                onRemove={(i) => patch({ success_criteria: form.success_criteria.filter((_, ix) => ix !== i) })}
                inputValue={succInput}
                setInputValue={setSuccInput}
                placeholder="How do we know they're doing well?"
                testid="success"
              />
            </div>
          )}

          {/* STEP 2 — Persona */}
          {step === 2 && (
            <div className="space-y-5" data-testid="step-persona-body">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1 block">Professional Background</label>
                <textarea
                  data-testid="persona-background"
                  value={form.persona.background}
                  onChange={(e) => patchP({ background: e.target.value })}
                  placeholder="e.g., 15 years in enterprise supply chain, worked at Nike and P&G, SAP-certified"
                  rows={2}
                  className="brut-input resize-none"
                />
              </div>
              <ChipList
                label="Areas of Expertise"
                items={form.persona.expertise}
                onAdd={(v) => patchP({ expertise: [...form.persona.expertise, v] })}
                onRemove={(i) => patchP({ expertise: form.persona.expertise.filter((_, ix) => ix !== i) })}
                inputValue={expertiseInput}
                setInputValue={setExpertiseInput}
                placeholder="e.g., Supply Chain, SAP, SQL"
                testid="expertise"
              />

              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3 block">Personality dials</label>
                <div className="space-y-4">
                  <Slider label="Creative ↔ Analytical" value={form.persona.sliders.analytical} onChange={(v) => patchSlider("analytical", v)} testid="analytical" left="Creative" right="Analytical" />
                  <Slider label="Concise ↔ Detailed"    value={form.persona.sliders.detail}     onChange={(v) => patchSlider("detail", v)}     testid="detail"     left="Concise"  right="Detailed" />
                  <Slider label="Casual ↔ Formal"       value={form.persona.sliders.formality}  onChange={(v) => patchSlider("formality", v)}  testid="formality"  left="Casual"   right="Formal" />
                  <Slider label="Deliberate ↔ Fast"     value={form.persona.sliders.speed}      onChange={(v) => patchSlider("speed", v)}      testid="speed"      left="Deliberate" right="Fast" />
                  <Slider label="Conservative ↔ Bold"   value={form.persona.sliders.risk}       onChange={(v) => patchSlider("risk", v)}       testid="risk"       left="Cautious" right="Bold" />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1 block">Free-form persona instructions (optional)</label>
                <textarea
                  data-testid="persona-freeform"
                  value={form.persona.freeform}
                  onChange={(e) => patchP({ freeform: e.target.value })}
                  placeholder="Extra personality notes, tone, quirks…"
                  rows={3}
                  className="brut-input resize-none"
                />
              </div>
            </div>
          )}

          {/* STEP 3 — Brain */}
          {step === 3 && (
            <div className="space-y-5" data-testid="step-brain-body">
              <div className="brut-card p-3 bg-amber-50 text-sm flex items-start gap-2">
                <Cpu size={16} strokeWidth={2.5} className="mt-0.5 text-amber-700 shrink-0" />
                <span>
                  <b>The brain is swappable.</b> You can change the model powering this worker anytime —
                  their persona, memory, and history stay intact.
                </span>
              </div>
              <BrainSelector
                value={form.brain.brain_id}
                onChange={(bid) => patchB({ brain_id: bid })}
              />
              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-200">
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1 block">Temperature: <span className="font-mono">{form.brain.temperature.toFixed(2)}</span></label>
                  <input type="range" min="0" max="2" step="0.1"
                    value={form.brain.temperature}
                    onChange={(e) => patchB({ temperature: parseFloat(e.target.value) })}
                    className="w-full accent-slate-900" data-testid="brain-temperature" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1 block">Max tokens: <span className="font-mono">{form.brain.max_tokens}</span></label>
                  <input type="range" min="256" max="4000" step="64"
                    value={form.brain.max_tokens}
                    onChange={(e) => patchB({ max_tokens: parseInt(e.target.value, 10) })}
                    className="w-full accent-slate-900" data-testid="brain-maxtokens" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 4 — Tools */}
          {step === 4 && (
            <ToolsStep form={form} patch={patch} />
          )}

          {/* STEP 5 — Review */}
          {step === 5 && (
            <ReviewStep form={form} />
          )}

          {error && (
            <div className="mt-4 text-sm font-semibold text-red-600 bg-red-100 border-2 border-red-500 rounded-lg p-2" data-testid="wizard-error">
              {error}
            </div>
          )}
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between mt-5">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="wizard-back"
            className="brut-btn brut-btn-outline disabled:opacity-30"
          >
            <ArrowLeft size={14} strokeWidth={2.5} /> Back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => stepValid && setStep((s) => s + 1)}
              disabled={!stepValid}
              data-testid="wizard-next"
              className="brut-btn"
            >
              Next <ArrowRight size={14} strokeWidth={2.5} />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              data-testid="wizard-submit"
              className="brut-btn"
              style={{ backgroundColor: "#10B981", borderColor: "#065F46" }}
            >
              {busy ? "Hiring…" : "🎉 Hire this worker"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Sub-components ---------- */

function ChipList({ label, items, onAdd, onRemove, inputValue, setInputValue, placeholder, testid }) {
  const add = () => { const v = inputValue.trim(); if (v) { onAdd(v); setInputValue(""); } };
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1 block">{label}</label>
      <div className="flex gap-2">
        <input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="brut-input flex-1"
          data-testid={`${testid}-input`}
        />
        <button type="button" onClick={add} className="brut-btn brut-btn-outline" data-testid={`${testid}-add`}>
          <Plus size={14} strokeWidth={2.5} />
        </button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {items.map((it, i) => (
            <span key={i} className="flex items-center gap-1 px-2.5 py-1 rounded-full border-2 border-slate-900 bg-white text-xs font-semibold" data-testid={`${testid}-chip-${i}`}>
              {it}
              <button type="button" onClick={() => onRemove(i)} className="text-slate-400 hover:text-red-500">
                <X size={12} strokeWidth={3} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Slider({ label, value, onChange, testid, left, right }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs font-bold text-slate-600 mb-1">
        <span>{label}</span>
        <span className="font-mono" data-testid={`slider-${testid}-value`}>{value}</span>
      </div>
      <input
        type="range" min="0" max="100" step="1"
        value={value} onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full accent-slate-900"
        data-testid={`slider-${testid}`}
      />
      <div className="flex justify-between text-[10px] text-slate-400 font-bold">
        <span>{left}</span><span>{right}</span>
      </div>
    </div>
  );
}

function ToolsStep({ form, patch }) {
  const [catalog, setCatalog] = useState([]);
  React.useEffect(() => {
    workforceApi.listToolsCatalog().then((d) => setCatalog(d.tools || [])).catch(() => {});
  }, []);
  const toggle = (id) => {
    const has = form.tools.includes(id);
    patch({ tools: has ? form.tools.filter((t) => t !== id) : [...form.tools, id] });
  };
  return (
    <div className="space-y-3" data-testid="step-tools-body">
      <p className="text-sm text-slate-600">Grant your worker the tools they need. You can change these anytime.</p>
      <div className="grid sm:grid-cols-2 gap-2.5">
        {catalog.map((t) => {
          const on = form.tools.includes(t.id);
          return (
            <button
              key={t.id}
              type="button"
              data-testid={`tool-${t.id}`}
              onClick={() => toggle(t.id)}
              className="text-left p-3 rounded-xl border-2 transition-all"
              style={{
                backgroundColor: on ? "#10B98118" : "#fff",
                borderColor: on ? "#059669" : "#CBD5E1",
                boxShadow: on ? "3px 3px 0 #059669" : "none",
                transform: on ? "translate(-1px,-1px)" : "none",
              }}
            >
              <div className="flex items-center gap-2">
                <span className="text-2xl">{t.icon}</span>
                <div className="flex-1">
                  <div className="font-heading font-bold text-sm">{t.name}</div>
                  <div className="text-xs text-slate-500 leading-snug">{t.description}</div>
                </div>
                {on && <Check size={16} strokeWidth={3} className="text-emerald-600 shrink-0" />}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ReviewStep({ form }) {
  return (
    <div className="space-y-4" data-testid="step-review-body">
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-2xl border-2 border-slate-900 bg-amber-100 flex items-center justify-center text-4xl">
          {form.avatar}
        </div>
        <div>
          <div className="font-heading font-bold text-2xl">{form.name || "Unnamed"}</div>
          <div className="text-slate-600">{form.title}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-0.5">{form.department} · {form.role_type}</div>
        </div>
      </div>
      {form.description && <p className="text-sm text-slate-700">{form.description}</p>}
      <div className="border-t border-slate-200 pt-3 grid sm:grid-cols-2 gap-3 text-sm">
        <div><b>Mission:</b> <span className="text-slate-700">{form.mission || "—"}</span></div>
        <div><b>Brain:</b> <span className="text-slate-700 font-mono">{form.brain.brain_id}</span> · T={form.brain.temperature.toFixed(2)}</div>
        <div><b>Tools:</b> <span className="text-slate-700">{form.tools.join(", ") || "none"}</span></div>
        <div><b>Responsibilities:</b> <span className="text-slate-700">{form.responsibilities.length}</span></div>
      </div>
      {form.persona.expertise.length > 0 && (
        <div className="text-sm"><b>Expertise:</b> <span className="text-slate-700">{form.persona.expertise.join(", ")}</span></div>
      )}
    </div>
  );
}
